/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 *
 * @see https://github.com/Azure-Samples/ms-identity-javascript-react-tutorial/blob/main/3-Authorization-II/2-call-api-b2c/SPA/src/authConfig.js
 */

import { Configuration, LogLevel } from "@azure/msal-browser";

export const _logLevelKeys = Object.values(LogLevel);

/**
 * Returns current log level
 */
function getLogLevel(): LogLevel {
    return (parseInt(import.meta.env.VITE_MSAL_LOG_LEVEL) as LogLevel) || LogLevel.Error;
}

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig: Configuration = {
    auth: {
        clientId: import.meta.env.VITE_MSAL_CLIENT_ID, // This is the ONLY mandatory field that you need to supply.
        authority: import.meta.env.VITE_MSAL_AUTHORITY, // Choose SUSI as your default authority.
        redirectUri: import.meta.env.VITE_MSAL_REDIRECT_URI, // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
        postLogoutRedirectUri: import.meta.env.VITE_MSAL_LOGOUT_REDIRECT_URI, // Indicates the page to navigate after logout.
        // In MSAL v5, navigateToLoginRequestUrl was removed from auth; pass it to handleRedirectPromise({ navigateToLoginRequestUrl: true }) if needed (default is true).
    },
    cache: {
        cacheLocation: "localStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        // storeAuthStateInCookie was removed in MSAL v5 (IE11/Edge workaround no longer supported).
    },
    system: {
        loggerOptions: {
            logLevel: getLogLevel(),
            loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
                if (containsPii) {
                    return;
                }
                return console.log(`[MSAL ${_logLevelKeys[level]}]`, message);
            },
        },
    },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest: { scopes: string[] } = {
    scopes: ["User.Read"],
};
